/*
 *   File : retirement.js
 *   Author : https://evoqins.com
 *   Description : Retirement detail page
 *   Integrations : NA
 *   Version : 1.0.0
*/

// *********** React Core ***************//
import { useState, useEffect } from "react";

// *********** Third Party ***************//
import { useLocation, useNavigate } from "react-router-dom";
import { Modal } from "bootstrap";
import { toast } from "react-toastify";

// *********** Components ***************//
import { CustomAmountInput, CustomSlider, CustomTextInput } from "../../Component/Form-Elements";
import { PrimaryButton } from "../../Component/Buttons";
import { Footer } from "../../Component/Footer";
import { BreadCrumbs } from "../../Component/Other";
import { Icon } from "../../Component/Icon";
import { ChangeBasketNameModal, GoalRetirementSnapshotModal } from "../../Component/Modal";
import GoalSummary from "./goal-summary";
import { APIService } from "../../Service";
import { _formatCorpus, _formatToRupees, _getAchieveByText, _getRetirementCorpus, _getRetirementSipCorpus, _getSavingsCorpus, _getRetirementRequiredSip, _setGoal } from "../../Helper";

// *********** Assets ***************//
import Loader from "../../Assets/Images/coloured_loader.svg";
import LeftPillarImage from "../../Assets/Images/GoalNew/goal-left-banner.png";
import RightPillarImage from "../../Assets/Images/GoalNew/goal-right-banner.png";
import MixTitleBgImage from "../../Assets/Images/GoalNew/asset-return-card-title-bg.png";
import CompareArrowIcon from "../../Assets/Images/GoalNew/compare-arrow.svg";

const BREADCRUMBS = [
    {
        label: "Home",
        path: "/"
    },
    {
        label: "Life goals",
        path: "/goals"
    },
];

const DEFAULT_CURRENT_AGE = "30";
const DEFAULT_RETIRE_AGE = "60";
const DEFAULT_MONTHLY_EXPENSES = 40000;
const DEFAULT_MONTHLY_SIP = 10000;
const DEFAULT_CURRENT_SAVINGS = 0;

const MAX_EXPENSES = 10000000;
const MAX_SIP = 500000;
const MAX_SAVINGS = 10000000;
const MAX_AGE = 100;
const EXPENSES_MIN = 1000;
const EXPENSES_STEP = 1000;
const SIP_MIN = 500;
const SIP_STEP = 500;

// Fixed assumption of the retirement corpus, the inflation rate comes from the goal detail
const CORPUS_MULTIPLIER = 22.5;
const DEFAULT_INFLATION_RATE = 6;

const STEP_UP_RATE = 10;
const MIN_TIMELINE_FILL_PERCENT = 2.7;

const FD_ASSET_MIX_ID = 1;
const IFSH_ASSET_MIX_ID = 3;

const ASSET_MIX_OPTIONS = [
    {
        id: FD_ASSET_MIX_ID,
        label: "FD’s",
        return_percentage: 6,
    },
    {
        id: 2,
        label: "Balanced",
        return_percentage: 9,
    },
    {
        id: IFSH_ASSET_MIX_ID,
        label: "IFSH Index",
        return_percentage: 12,
    },
];

const Retirement = () => {

    const location = useLocation();
    const navigate = useNavigate();

    const [goalId, setGoalId] = useState(location.state?.goal_id ?? "");
    const [goalDetail, setGoalDetail] = useState({});
    const [loading, setLoading] = useState(true);

    const [currentAge, setCurrentAge] = useState(DEFAULT_CURRENT_AGE);
    const [retireAge, setRetireAge] = useState(DEFAULT_RETIRE_AGE);
    const [monthlyExpenses, setMonthlyExpenses] = useState(DEFAULT_MONTHLY_EXPENSES);
    const [monthlySip, setMonthlySip] = useState(DEFAULT_MONTHLY_SIP);
    const [currentSavings, setCurrentSavings] = useState(DEFAULT_CURRENT_SAVINGS);
    const [isStepUpSip, setIsStepUpSip] = useState(false);
    const [selectedAssetMixId, setSelectedAssetMixId] = useState(IFSH_ASSET_MIX_ID);
    const [savedCorpus, setSavedCorpus] = useState(0);
    const [showSnapshotModal, setShowSnapshotModal] = useState(false);
    const [isSavingGoal, setIsSavingGoal] = useState(false);
    const [savedGoalId, setSavedGoalId] = useState(null);
    const [goalName, setGoalName] = useState("");
    const [showChangeNameModal, setShowChangeNameModal] = useState(false);
    const [isNameUpdating, setIsNameUpdating] = useState(false);
    const [goalSummary, setGoalSummary] = useState(false);

    // Age limits come from the goal detail
    const currentAgeLimit = goalDetail.admin_configured?.meta_data?.current_age;
    const retireAgeLimit = goalDetail.admin_configured?.meta_data?.retirement_age;
    const minCurrentAge = currentAgeLimit?.min_age ?? 0;
    const maxCurrentAge = currentAgeLimit?.max_age ?? MAX_AGE;
    const minRetireAge = retireAgeLimit?.min ?? 0;
    const maxRetireAge = retireAgeLimit?.max ?? MAX_AGE;

    // Inflation rate (%) comes from the goal detail
    const inflationRate = goalDetail.admin_configured?.meta_data?.inflation_rate ?? DEFAULT_INFLATION_RATE;

    let currentAgeError = "";
    if (currentAge !== "" && Number(currentAge) < minCurrentAge) {
        currentAgeError = `Minimum age is ${minCurrentAge}`;
    }

    let retireAgeError = "";
    if (retireAge !== "" && Number(retireAge) < minRetireAge) {
        retireAgeError = `Minimum age is ${minRetireAge}`;
    } else if (currentAge !== "" && retireAge !== "" && Number(retireAge) <= Number(currentAge)) {
        retireAgeError = "Retire age should be greater than current age";
    }

    const retirementYears = Number(retireAge) - Number(currentAge);

    // The corpus saved on the goal is shown until the user changes the ages or the expenses, then it is calculated
    const calculatedCorpus = _getRetirementCorpus({
        current_age: currentAge,
        retirement_age: retireAge,
        monthly_expenses: monthlyExpenses,
        inflation_rate: inflationRate,
        corpus_multiplier: CORPUS_MULTIPLIER,
    });
    const retirementCorpus = savedCorpus > 0 ? savedCorpus : calculatedCorpus;

    useEffect(() => {

        window.scrollTo({
            top: 0, behavior: 'smooth'
        });

        if (location.state !== null) {
            setGoalId(location.state.goal_id);
        } else {
            navigate("/goals");
        };

        if (location.state?.is_summary === false) {
            setGoalSummary(false);
        }

    }, [location]);

    useEffect(() => {
        if (goalId !== "") {
            _getGoalDetails();
        }
    }, [goalId]);

    // The summary step is kept in the url, so that a reload stays on it
    useEffect(() => {
        if (goalSummary === true) {
            navigate(`${location.pathname}#configure`, {
                state: {
                    goal_id: goalId,
                }
            });
        }
    }, [goalSummary]);

    useEffect(() => {
        if (showChangeNameModal) {
            new Modal(document.getElementById("change-basket-name-modal")).show();
        }
    }, [showChangeNameModal]);

    useEffect(() => {
        if (showSnapshotModal) {
            new Modal(document.getElementById("goal-retirement-snapshot-modal")).show();
        }
    }, [showSnapshotModal]);

    const breadcrumbs = [...BREADCRUMBS, { label: goalDetail.admin_configured?.name }];

    const selectedAssetMix = ASSET_MIX_OPTIONS.find((option) => option.id === selectedAssetMixId);
    const sipToday = Number(monthlySip) || 0;
    const savingsAmount = Number(currentSavings) || 0;

    const retirementCorpusText = _formatCorpus(retirementCorpus);
    const achieveByText = _getAchieveByText(retirementYears);

    // The timeline fills as the current savings grow towards the retirement corpus
    let savingsProgressPercent = 0;
    if (retirementCorpus > 0) {
        savingsProgressPercent = Math.min((Number(currentSavings) / retirementCorpus) * 100, 100);
    }
    const timelineFillPercent = Math.max(savingsProgressPercent, MIN_TIMELINE_FILL_PERCENT);

    // The asset mix section is calculated from the selected return before retirement
    const years = Math.max(retirementYears, 0);
    const assetMixReturn = selectedAssetMix.return_percentage;
    const savingsCorpus = _getSavingsCorpus(currentSavings, assetMixReturn, years);

    const flatSipCorpus = _getRetirementSipCorpus(sipToday, assetMixReturn, years, 0);
    const stepUpSipCorpus = _getRetirementSipCorpus(sipToday, assetMixReturn, years, STEP_UP_RATE);
    const requiredFlatSip = _getRetirementRequiredSip(retirementCorpus, currentSavings, assetMixReturn, years, 0);
    const requiredStepUpSip = _getRetirementRequiredSip(retirementCorpus, currentSavings, assetMixReturn, years, STEP_UP_RATE);

    // The goal is funded when the flat SIP reaches the corpus, the step-up only lowers the SIP needed
    const projectedCorpus = savingsCorpus + flatSipCorpus;
    const isGoalFunded = retirementCorpus > 0 && projectedCorpus >= retirementCorpus;
    const sipNeededForGoal = isStepUpSip ? requiredStepUpSip : requiredFlatSip;
    const sipNeeded = isGoalFunded ? 0 : sipNeededForGoal;
    const sipGap = Math.max(sipNeeded - sipToday, 0);

    // Figures the snapshot modal shows for the selected asset mix
    const plan = {
        years: years,
        step_up_rate: STEP_UP_RATE,
        is_step_up_applied: isStepUpSip,
        target_corpus: retirementCorpus,
        projected_corpus: projectedCorpus,
        step_up_corpus: savingsCorpus + stepUpSipCorpus,
        required_flat_sip: requiredFlatSip,
        required_step_up_sip: requiredStepUpSip,
        additional_flat_sip: Math.max(requiredFlatSip - sipToday, 0),
    };

    //handle back
    const _handleBack = () => {
        navigate("/goals");
    };

    const _handleCurrentAgeChange = (value) => {
        setCurrentAge(value);
        setSavedCorpus(0);
    };

    const _handleRetireAgeChange = (value) => {
        setRetireAge(value);
        setSavedCorpus(0);
    };

    const _handleMonthlyExpensesChange = (value) => {
        setMonthlyExpenses(value);
        setSavedCorpus(0);
    };

    const _handleMonthlySipChange = (value) => {
        setMonthlySip(value);
    };

    const _handleCurrentSavingsChange = (value) => {
        setCurrentSavings(value);
    };

    const _handleToggleStepUpSip = () => {
        setIsStepUpSip((isEnabled) => !isEnabled);
    };

    const _handleSelectAssetMix = (id) => {
        setSelectedAssetMixId(id);
    };

    const _handleViewSnapshot = () => {
        if (currentAge === "" || retireAge === "" || currentAgeError !== "" || retireAgeError !== "") {
            toast.dismiss();
            toast.error("Enter a valid current age and retirement age", { className: "e-toast" });
            return;
        }

        setShowSnapshotModal(true);
    };

    const _handleCloseSnapshot = () => {
        setShowSnapshotModal(false);
    };

    const _handleOpenChangeNameModal = () => {
        setShowChangeNameModal(true);
    };

    const _handleChangeGoalName = (name) => {
        _renameGoal(name, false);
    };

    const _handleSkipGoalName = () => {
        _renameGoal("", true);
    };

    const _handleCloseChangeNameModal = () => {
        setIsNameUpdating(false);
        setShowChangeNameModal(false);
    };

    // API - Rename goal
    const _renameGoal = (name, skip) => {
        let api_version = 3;
        let is_post = true;
        let url = "goal/rename";
        let data = {
            "goal_id": savedGoalId,
            "skip": true,
        };

        if (skip === false) {
            data = {
                "goal_id": savedGoalId,
                "name": name,
                "skip": false,
            };
        }

        setIsNameUpdating(true);

        APIService(is_post, url, data, null, api_version).then((response) => {
            if (response.status_code === 200) {
                if (skip === false) {
                    setGoalName(name);
                }
                document.getElementById("change-basket-name-modal-close")?.click();
                setGoalSummary(true);
            } else {
                toast.dismiss();
                toast.error(response?.message ?? "Something went wrong", { className: "e-toast" });
            }
            setIsNameUpdating(false);
        });
    };

    // API - Save the retirement goal, the current savings go as the lumpsum
    const _handleStartGoal = () => {
        if (retirementCorpus <= 0) {
            toast.dismiss();
            toast.error("Enter valid details to calculate your retirement corpus", { className: "e-toast" });
            return;
        }

        if (sipToday === 0 && savingsAmount === 0) {
            toast.dismiss();
            toast.error("Either monthly SIP or current savings is mandatory", { className: "e-toast" });
            return;
        }

        if (savingsAmount > retirementCorpus) {
            toast.dismiss();
            toast.error("Retirement corpus should be greater than current savings", { className: "e-toast" });
            return;
        }

        const params = {
            "goal_id": goalId,
            "target": retirementCorpus,
            "sip": sipToday || null,
            "lumpsum": savingsAmount || null,
            "sip_tenure": retirementYears,
            "lumpsum_select": 1,
            // The corpus is already worked out in the inflated value of the retirement year
            "inflation_select": false,
            "additional_data": {
                "add_lumpsum_now": savingsAmount > 0,
                "current_age": Number(currentAge),
                "retirement_age": Number(retireAge),
                "monthly_expenses": Number(monthlyExpenses),
                "monthly_sip": sipToday,
                "step_up_enabled": isStepUpSip,
                "return_rate": assetMixReturn,
            },
        };

        setIsSavingGoal(true);

        _setGoal(params).then((response) => {
            if (response.status_code === 200) {
                setSavedGoalId(response.data.goal_id);
                toast.dismiss();
                toast.success("Goal saved", { className: "e-toast" });

                // The goal name modal opens once the snapshot modal is fully closed
                document.getElementById("goal-retirement-snapshot-modal")?.addEventListener("hidden.bs.modal", _handleOpenChangeNameModal, { once: true });
                document.getElementById("goal-retirement-snapshot-modal-close")?.click();
            } else {
                toast.dismiss();
                toast.error(response?.message ?? "Something went wrong", { className: "e-toast" });
            }
            setIsSavingGoal(false);
        });
    };

    // Footnote of the gap card: the surplus when the goal is funded, else what the SIP needs (flat or with the step-up)
    const _renderGapNote = () => {
        if (retirementCorpus <= 0) {
            return null;
        }

        if (isGoalFunded) {
            return <>You have a <span className="e-font-weight-700">₹{_formatToRupees(projectedCorpus - retirementCorpus)}</span> corpus surplus at retirement under these assumptions.</>;
        }

        if (isStepUpSip && sipGap === 0) {
            return <>You are already investing more than the starting SIP required for this strategy.</>;
        }

        if (isStepUpSip) {
            return <>A {STEP_UP_RATE}% annual step-up lowers the starting SIP by <span className="e-font-weight-700">₹{_formatToRupees(requiredFlatSip - requiredStepUpSip)}</span> vs. keeping it flat.</>;
        }

        return <>That means <span className="e-font-weight-700">₹{_formatToRupees(sipGap)}</span> more than your current SIP today.</>;
    };

    //API- Post goal detail
    const _getGoalDetails = () => {
        let is_post = true;
        let url = 'goal/get';
        let data = {
            "goal_id": goalId,
            "is_invested": false
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setGoalDetail(response.data);
                _fillGoalInputs(response.data);

                // Preserving current step if user reloads page
                if (location.hash) {
                    setGoalSummary(true);
                }
            } else {
                setGoalDetail({});
            }
            setLoading(false);
        });
    };

    // Fills the inputs from the saved goal (user_configured), the ages fall back to the admin defaults
    const _fillGoalInputs = (goal) => {
        const user_configured = goal.user_configured || {};
        const saved_inputs = user_configured.additional_data || {};
        const meta_data = goal.admin_configured?.meta_data;

        setCurrentAge(String(saved_inputs.current_age || meta_data?.current_age?.default || DEFAULT_CURRENT_AGE));
        setRetireAge(String(saved_inputs.retirement_age || meta_data?.retirement_age?.default || DEFAULT_RETIRE_AGE));
        setMonthlyExpenses(saved_inputs.monthly_expenses || DEFAULT_MONTHLY_EXPENSES);
        setMonthlySip(saved_inputs.monthly_sip || user_configured.sip || DEFAULT_MONTHLY_SIP);
        setSavedCorpus(user_configured.target_amount || 0);
    };

    // Stays rendered on the summary step until the modal has finished closing
    const changeNameModal = showChangeNameModal &&
        <ChangeBasketNameModal title="Change goal name"
            skipButtonName="Skip"
            basketName={goalName || goalDetail.admin_configured?.name || ""}
            loading={isNameUpdating}
            onChangeName={_handleChangeGoalName}
            onSkip={_handleSkipGoalName}
            onClose={_handleCloseChangeNameModal} />;

    // Goal summary step
    if (goalSummary) {
        return (
            <>
                <GoalSummary goal_name={goalDetail.admin_configured?.short_name}
                    savedGoalId={savedGoalId}
                    customGoalName={goalName}
                    isLumpsum={savingsAmount > 0}
                    tenure={{ min: 1, max: maxCurrentAge - minCurrentAge }}
                    inflationSelect={false} />

                {changeNameModal}

                <Footer />
            </>
        );
    }

    return (
        <>
            <div className="row min-h-100vh pb-4 pb-sm-5 e-bg-lotion px-4 px-sm-5 ps-lg-4 pe-lg-0 padding-26px-top e-border-radius-TL-BL-24px">

                {loading ?
                    <div className="min-h-100vh text-center mt-5" >
                        <img src={Loader}
                            draggable="false"
                            alt="loader" />
                    </div>
                    :
                    <div className="col-lg-11 p-0 pb-4 pb-sm-5">
                        {/* breadcrumbs */}
                        <BreadCrumbs data={breadcrumbs} />

                        {/* Page title */}
                        <header className="d-flex align-items-center gap-sm-3 gap-2 my-sm-4 my-3 ">
                            <button type="button"
                                className="d-flex p-0 no-border no-outline e-bg-transparent cursor-pointer"
                                aria-label="Back to life goals"
                                onClick={_handleBack}>
                                <Icon icon="arrow-back-left" size={24} className="width-20px-res" />
                            </button>
                            <h1 className="mb-0 e-text-chinese-black e-alt-font-poppins e-font-weight-400 e-font-14-res e-font-16 line-height-24px">
                                {goalDetail.admin_configured?.name}
                            </h1>
                        </header>

                        <div className="row gx-4 gy-4 align-items-start">

                            {/* Retirement plan */}
                            <div className="col-md-6">
                                <section className="retirement-goal-card e-bg-white position-relative overflow-hidden pb-4">
                                    <img src={LeftPillarImage}
                                        width={70}
                                        height={455}
                                        className="retirement-pillar retirement-pillar-left d-none d-sm-block"
                                        draggable="false"
                                        alt="" />
                                    <img src={RightPillarImage}
                                        width={70}
                                        height={455}
                                        className="retirement-pillar retirement-pillar-right d-none d-sm-block"
                                        draggable="false"
                                        alt="" />

                                    <div className="retirement-goal-content position-relative px-sm-4">
                                        <header className="px-0 px-sm-5 pt-sm-3 text-center">
                                            <h2 className="mb-2 e-text-eerie-black e-primary-font-inter e-font-weight-600 e-font-18 line-height-30px">
                                                Build the <span className="e-heading-font-ivy e-font-weight-400 e-text-cobalt-ink">retirement</span> income you need.
                                            </h2>
                                            <p className="mb-0 e-text-charleston-green e-alt-font-poppins e-font-weight-400 e-font-13 e-font-12-res">
                                                Target monthly income in today’s value accounts for healthcare, utilities, insurance, travel and regular living expenses.
                                            </p>
                                        </header>

                                        {/* Projection */}
                                        <article className="retirement-projection e-border-1px-baby-blue e-border-radius-28 p-3 p-sm-4 mt-4">
                                            <div className="d-flex justify-content-between gap-3">
                                                <div>
                                                    <p className="margin-6px-bottom e-text-jet-gray e-primary-font-inter e-font-weight-400 e-font-14 line-height-18px">
                                                        Invest
                                                    </p>
                                                    <p className="mb-0 e-text-vivid-azure e-primary-font-inter e-font-weight-800 e-font-16 line-height-20px">
                                                        ₹{_formatToRupees(sipToday)}
                                                        <span className="ms-1 e-text-jet-gray e-font-weight-500 e-font-12 line-height-16px">
                                                            monthly
                                                        </span>
                                                    </p>
                                                </div>
                                                <div className="text-end">
                                                    <p className="margin-6px-bottom e-text-jet-gray e-primary-font-inter e-font-weight-400 e-font-14 line-height-18px">
                                                        Retirement corpus
                                                    </p>
                                                    <p className="mb-0 e-text-vivid-azure e-primary-font-inter e-font-weight-800 e-font-16 line-height-20px">
                                                        ₹{retirementCorpusText.replace(" ", "")}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="retirement-timeline mt-4"
                                                role="progressbar"
                                                aria-label="Current savings compared to the retirement corpus"
                                                aria-valuemin={0}
                                                aria-valuemax={100}
                                                aria-valuenow={Math.round(savingsProgressPercent)}>
                                                <span className="retirement-timeline-fill"
                                                    style={{ width: `${timelineFillPercent}%` }}></span>
                                            </div>
                                            <p className="d-flex justify-content-between gap-2 mt-2 mb-0 e-text-dim-ash e-primary-font-inter e-font-weight-400 e-font-14 e-font-12-res line-height-18px">
                                                <span>Start today</span>
                                                <span>
                                                    Achieve by <span className="e-text-ink-gray e-font-weight-500">{achieveByText}</span>
                                                </span>
                                            </p>

                                            {/* Step-up SIP */}
                                            <label htmlFor="retirement-step-up-sip"
                                                className="d-flex justify-content-between align-items-center gap-3 e-bg-white e-border-radius-24 e-box-shadow-soft-card p-3 mt-3 cursor-pointer">
                                                <span>
                                                    <span className="d-block e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-12 line-height-16px">
                                                        Make your ₹{retirementCorpusText} goal easier
                                                    </span>
                                                    <span className="d-block mt-1 e-text-smoky-black-60 e-primary-font-inter e-font-weight-300 e-font-12 line-height-16px">
                                                        Start smaller, increase your SIP by 10% yearly.
                                                    </span>
                                                </span>
                                                <span className="form-check form-switch mb-0 ps-0 d-flex align-items-center">
                                                    <input id="retirement-step-up-sip"
                                                        className="cursor-pointer form-check-input toggle-40x20 flex-shrink-0 m-0 shadow-none"
                                                        type="checkbox"
                                                        role="switch"
                                                        checked={isStepUpSip}
                                                        onChange={_handleToggleStepUpSip} />
                                                </span>
                                            </label>
                                        </article>
                                    </div>
                                </section>

                                {/* Inputs */}
                                <div className="d-flex flex-column gap-4 px-4">
                                    <div className="row gx-3 gx-sm-4 gy-3 gy-sm-0">
                                        <div className="col-12 col-sm-6">
                                            <CustomTextInput label="Current age"
                                                variant={4}
                                                type="number"
                                                suffix="Year(s)"
                                                value={currentAge}
                                                maxLength={3}
                                                maxValue={maxCurrentAge}
                                                hasBottomMargin={false}
                                                borderClassName="e-border-1px-white-marble"
                                                inputWrapperBorderStyle="e-border-radius-12"
                                                errorMsg={currentAgeError}
                                                successMsg=""
                                                handleChange={_handleCurrentAgeChange} />
                                        </div>
                                        <div className="col-12 col-sm-6">
                                            <CustomTextInput label="Retire at"
                                                variant={4}
                                                type="number"
                                                suffix="Year(s)"
                                                value={retireAge}
                                                maxLength={3}
                                                maxValue={maxRetireAge}
                                                hasBottomMargin={false}
                                                borderClassName="e-border-1px-white-marble"
                                                inputWrapperBorderStyle="e-border-radius-12"
                                                errorMsg={retireAgeError}
                                                successMsg=""
                                                handleChange={_handleRetireAgeChange} />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="d-flex justify-content-between align-items-center gap-2">
                                            <span className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-16 line-height-20px">
                                                Monthly expenses
                                            </span>
                                            <CustomAmountInput variant={4}
                                                className="w-190px flex-shrink-0"
                                                inputClassName="e-bg-white"
                                                borderClassName="e-border-1px-gainsboro-gray"
                                                value={monthlyExpenses}
                                                maxValue={MAX_EXPENSES}
                                                errorMsg=""
                                                handleChange={_handleMonthlyExpensesChange} />
                                        </label>
                                        <div className="h-4 margin-top-20px mb-2">
                                            <CustomSlider type={2}
                                                min={EXPENSES_MIN}
                                                max={MAX_EXPENSES}
                                                step={EXPENSES_STEP}
                                                defaultValue={Number(monthlyExpenses)}
                                                handleChange={_handleMonthlyExpensesChange} />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="d-flex justify-content-between align-items-center gap-2">
                                            <span className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-16 line-height-20px">
                                                Monthly SIP
                                            </span>
                                            <CustomAmountInput variant={4}
                                                className="w-190px flex-shrink-0"
                                                inputClassName="e-bg-white"
                                                borderClassName="e-border-1px-gainsboro-gray"
                                                value={monthlySip}
                                                maxValue={MAX_SIP}
                                                errorMsg=""
                                                handleChange={_handleMonthlySipChange} />
                                        </label>
                                        <div className="h-4 margin-top-20px mb-2">
                                            <CustomSlider type={2}
                                                min={SIP_MIN}
                                                max={MAX_SIP}
                                                step={SIP_STEP}
                                                defaultValue={Number(monthlySip)}
                                                handleChange={_handleMonthlySipChange} />
                                        </div>
                                    </div>

                                    <label className="d-flex justify-content-between align-items-center gap-2">
                                        <span className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-16 line-height-20px">
                                            Current savings
                                        </span>
                                        <CustomAmountInput variant={4}
                                            className="w-190px flex-shrink-0"
                                            inputClassName="e-bg-white"
                                            borderClassName="e-border-1px-gainsboro-gray"
                                            value={currentSavings}
                                            maxValue={MAX_SAVINGS}
                                            errorMsg=""
                                            handleChange={_handleCurrentSavingsChange} />
                                    </label>
                                </div>

                            </div>

                            {/* Asset mix and return */}
                            <div className="col-md-6">
                                <section className="retirement-mix-card e-border-radius-16 position-relative overflow-hidden px-3 px-sm-4 pt-5 pb-4">
                                    <img src={MixTitleBgImage}
                                        width={529}
                                        height={118}
                                        className="retirement-mix-arc"
                                        draggable="false"
                                        alt="" />

                                    <h2 className="position-relative text-center margin-32px-bottom e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-14-res e-font-16 line-height-20px">
                                        <span className="retirement-mix-highlight e-heading-font-ivy e-font-weight-400 e-text-royal-azure">Pre-retirement</span> asset mix & return
                                    </h2>

                                    <ul className="position-relative d-flex gap-2 gap-sm-3 list-unstyled mb-0">
                                        {
                                            ASSET_MIX_OPTIONS.map((option) => (
                                                <li key={option.id} className="flex-fill">
                                                    <button type="button"
                                                        className={`goal-tab ${option.id === selectedAssetMixId ? "active" : ""} w-100 e-border-radius-8 py-2 px-1 cursor-pointer no-outline e-primary-font-inter`}
                                                        aria-pressed={option.id === selectedAssetMixId}
                                                        onClick={() => _handleSelectAssetMix(option.id)}>
                                                        <span className="d-block e-font-weight-500 e-font-14 e-font-12-res line-height-18px">
                                                            {option.label}
                                                        </span>
                                                        <span className="d-block opacity-8 e-font-weight-400 e-font-12 e-font-10-res line-height-16px">
                                                            {option.return_percentage}% p.a
                                                        </span>
                                                    </button>
                                                </li>
                                            ))
                                        }
                                    </ul>

                                    {/* The gap */}
                                    <article className="e-bg-asset-mix-card e-border-radius-16 position-relative padding-16px-all mt-4">
                                        <h3 className="mb-3 e-text-black e-primary-font-inter e-font-weight-600 e-font-12 line-height-16px">
                                            THE GAP, TRANSLATED INTO ACTION
                                        </h3>
                                        <p className="d-flex justify-content-between align-items-center gap-2 mb-0 e-text-jet-gray e-primary-font-inter e-font-weight-400">
                                            <span className="e-font-12 line-height-16px">Your SIP today</span>
                                            <span className="e-font-14 line-height-18px">
                                                <span className="e-text-azure-blue e-font-weight-800">₹{_formatToRupees(sipToday)}</span> /m
                                            </span>
                                        </p>
                                        <img src={CompareArrowIcon}
                                            width={24}
                                            height={24}
                                            className="d-block mx-auto my-2"
                                            draggable="false"
                                            alt="" />
                                        <p className="d-flex justify-content-between align-items-center gap-2 mb-0 e-text-jet-gray e-primary-font-inter e-font-weight-400">
                                            <span className="e-font-12 line-height-16px">SIP needed to reach your goal</span>
                                            <span className="e-font-14 line-height-18px">
                                                <span className="e-text-azure-blue e-font-weight-800">₹{_formatToRupees(sipNeeded)}</span> /m
                                            </span>
                                        </p>
                                        <p className="mt-3 mb-0 fst-italic e-text-azure-link e-primary-font-inter e-font-weight-400 e-font-12 line-height-16px">
                                            {_renderGapNote()}
                                        </p>
                                    </article>
                                </section>
                            </div>
                        </div>

                        {/* Snapshot */}
                        <div className="col-10 col-sm-6 col-lg-4 mx-auto margin-32px-top pt-4">
                            <PrimaryButton name="View Full Retirement Snapshot"
                                className="w-100 padding-12px-top padding-12px-bottom e-font-12-res"
                                onPress={_handleViewSnapshot} />
                        </div>
                    </div>
                }
            </div>

            {/* Retirement snapshot modal */}
            {
                showSnapshotModal &&
                <GoalRetirementSnapshotModal plan={plan}
                    desiredIncome={monthlyExpenses}
                    currentSip={sipToday}
                    assumedReturn={assetMixReturn}
                    isStarting={isSavingGoal}
                    onStart={_handleStartGoal}
                    onClose={_handleCloseSnapshot} />
            }

            {changeNameModal}

            {/* footer  */}
            <Footer />
        </>
    )
}
export default Retirement;
